import { REACT_APP_BACK_END_URL } from 'config'
import { useCallback } from 'react'
import type {
  RecordsTableMenuItemAction,
  RecordsTableProps,
} from '../components/RecordsTable/types'
import type { WorkflowRecord } from '../types'

type UseWorkflowRecordSharePointOptions = {
  projectId: string
  sharePointEnabled: boolean
  /** Opens file management; omit it to hide the Upload Files action. */
  onManageFiles?: (record: WorkflowRecord) => void
}

/**
 * Resolves SharePoint menu actions for workflow records.
 *
 * View Folder is permission-independent; its REST endpoint resolves the current
 * SharePoint web URL at navigation time. Upload Files appears only when the
 * server reports file management as enabled and authorized for the user.
 */
export function useWorkflowRecordSharePoint({
  projectId,
  sharePointEnabled,
  onManageFiles,
}: UseWorkflowRecordSharePointOptions): RecordsTableProps['actions'] {
  return useCallback(
    (record) => {
      if (!sharePointEnabled) return {}

      const pluginData = record.workflowPluginData.records[0]?.data
      const folderDriveItemId =
        pluginData?.type === 'sharepoint' ? pluginData.driveItemId : null
      const viewFolder = folderDriveItemId
        ? {
            disabled: false as const,
            href: `${REACT_APP_BACK_END_URL}/api/projects/${encodeURIComponent(
              projectId
            )}/sharepoint/items/${encodeURIComponent(folderDriveItemId)}`,
          }
        : {
            disabled: {
              message: `${record.recordName} does not have an associated SharePoint folder.`,
            },
          }

      const items: RecordsTableMenuItemAction[] = [
        {
          availableWhenDeleted: true,
          label: 'View Folder',
          permission: true,
          ...viewFolder,
        },
      ]
      if (
        record.fileManagementOptions?.enabled &&
        record.fileManagementOptions.authorized &&
        !record.deleted &&
        onManageFiles
      )
        items.push({
          label: 'Upload Files',
          permission: true,
          disabled: record.fileManagementOptions.disabledReason
            ? {
                message:
                  'This record does not have an available SharePoint folder.',
              }
            : false,
          onClick: () => onManageFiles(record),
        })
      return { sharePoint: { disabled: false, items } }
    },
    [projectId, sharePointEnabled, onManageFiles]
  )
}
